//#region Angular Imports

import { IQService } from 'angular'

//#endregion

//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExtreme Imports

import { dxButtonOptions } from 'devextreme/ui/button'
import dxPopover, { dxPopoverOptions } from 'devextreme/ui/popover'
import { dxSelectBoxOptions } from 'devextreme/ui/select_box'

//#endregion

//#region Helper Imports

import { ConvertHelper } from '../../../helpers/convert-helper'
import { CreateEditInitParams, CreateEditPopupOptions } from '../create-edit-popup/create-edit-popup.controller'
import { EventsNames } from '../../../helpers/events-names'
import { FileComponent } from '../../../helpers/file-component'
import { LocalizationHelper } from "../../../helpers/localization-helper"
import { ToastHelper } from '../../../helpers/toast-helper'
import { LogHelper } from '../../../helpers'
import { DataProviderRequestError } from '../../../data-provider'

//#endregion

//#region Model Imports

import { ColumnInfo } from '../../../models/custom'
import { Enums } from "../../../models/common/enums"
import { IOpObject, IOpData } from '../../../models/util/interfaces'
import { OpDataType, OpDataTypeInGroup, OpDescr, OpFile } from '../../../models/core'
import { OpExportData, OpExportPlugin, OpExportPluginParam } from '../../../models/util'

//#endregion

//#region Service Imports

import { ExportService, DataExporter, ExportDataMethod } from '../../../services/export'
import { UserSessionService } from '../../../services/user-session'
import { DataTypeService } from '../../../services/data-type'

//#endregion

import { CreateEditSection, CommonCreateEdit } from '../common.create-edit'
import { IMRBaseControlDirectiveController } from '../base'


// #region Options

export type ExportFilterMethod = (referenceType: Enums.ReferenceType, columns: ColumnInfo[], customWhereClause: string, dllName: string, params: OpExportPluginParam[]) => JQueryPromise<OpFile>

export type ExportFormatValueArgs = {
    value: any,
    columnName: string
}

export type ExportOptions = {
    referenceType: Enums.ReferenceType

    /**
     * Params options have to be specified when configuring exports with plugins.
     */
    customWhere?: string,

    /**
     * Name of the dll that should perform the default export.
     */
    defaultExportDLL?: string


    pluginOptions?: {
        moduleName: Enums.SimaxModuleName,
        popupName: string,

        popoverTarget?: any
    }

    onInitialized?: (exporter: ExportController) => void
    onExportStarted?: () => void
    onExportSucceeded?: (file: OpFile) => void
    onExportFinished?: () => void
    onExportError?: (error: any) => void

    formatValue?: (e: ExportFormatValueArgs) => string
    exportDataMethod?: ExportDataMethod
    customExportMethod?: ExportFilterMethod
}

// #endregion

// #region Export scope

export interface ExportScope extends ng.IScope {

    options: ExportOptions
    paramsCreateEditOptions: CreateEditPopupOptions

    // #region Dx Options

    configurationDownloadButtonOptions: dxButtonOptions
    configurationPopoverOptions: dxPopoverOptions
    configurationPluginSelectBoxOptions: dxSelectBoxOptions

    // #endregion
}

// #endregion

export class ExportController extends IMRBaseControlDirectiveController {

    // #region Consts

    private static get DEFAULT_EXPORT_DLL(): string { return 'Controls.Reports' }
    private static LOG_PREFIX = '[SIMAX.Common.Export.Controller]'

    // #endregion

    // #region Members

    private configurationPopover: dxPopover = null

    private defaultPlugin: OpExportPlugin = null
    private currentPlugin: OpExportPlugin = null
    private loadedPlugins: OpExportPlugin[] = null

    private currentParams: OpExportPluginParam[] = null
    private referenceType: Enums.ReferenceType = null
    private columnInfos: ColumnInfo[] = null
    private whereClause: string = null
    private paramDataTypes: OpDataType[] = null

    // #endregion

    // #region Ctor

    constructor(
        private readonly $scope: ExportScope,
        private readonly $q: IQService,
        private readonly dataTypeService: DataTypeService,
        private readonly exportService: ExportService,
        private readonly userSessionService: UserSessionService) {
        super()

        if ($scope.options != null) {
            this.initialize($scope.options)
        }
        else {
            LogHelper.warn(ExportController.LOG_PREFIX, "No options given.")
        }
    }

    // #endregion

    // #region Lifecycle

    private initialize(options: ExportOptions): void {
        this.referenceType = options.referenceType

        this.defaultPlugin = this.createDefaultPlugin()
        this.currentPlugin = this.defaultPlugin

        if (options.pluginOptions != null) {

            this.loadPlugins(options)
                .then((plugins: OpExportPlugin[]) => {
                    this.loadedPlugins = plugins

                    if (_.size(plugins) > 0) {
                        this.setPluginPopoverOptions(options, this.loadedPlugins, this.defaultPlugin)
                        this.setParamsPopupOptions(options)
                    }
                    else {
                        LogHelper.info(
                            ExportController.LOG_PREFIX,
                            `No plugins for the given options. Only default export is available for referenceType: ${ConvertHelper.enumToString(Enums.ReferenceType, options.referenceType)}.`
                        )
                    }

                })
                .then(() => {
                    this.onInitialized()
                })
                .fail((error: any) => {
                    LogHelper.warn(ExportController.LOG_PREFIX, `Unable to configure the exporter. Error: ${ConvertHelper.getRequestErrorMessage(error)}.`)
                })
        }
        else {
            LogHelper.info(ExportController.LOG_PREFIX, "No params options given. Only default export is available.")

            this.onInitialized()
        }
    }

    // #endregion

    // #region Public Interface

    public exportFilter(columnInfos: ColumnInfo[], whereClause: string): void {
        this.resetExport(columnInfos, this.createCustomWhereClause(whereClause))

        if (_.size(this.loadedPlugins) > 0) {
            this.toggleConfigurationPopover()
        }
        else {
            this.startExport()
        }
    }

    public getDataExporter(): DataExporter {
        return this.exportService.createDataExporter(this.$scope.options.exportDataMethod)
    }

    public exportData(dataExporter: DataExporter) {
        if (dataExporter != null) {
            this.onExportStarted()

            dataExporter
                .export()
                .then((file: OpFile) => {
                    this.onExportSucceeded(file)
                    this.saveFile(file)
                })
                .fail((error: DataProviderRequestError) => {
                    this.onExportError(error)

                    ToastHelper.showError(error.toString())
                    LogHelper.warn(ExportController.LOG_PREFIX, `Unable to export visible data data.`, error.toString())
                })
                .always(() => {
                    this.onExportFinished()
                })
        }
    }

    // #endregion

    // #region Custom Where Clause

    private createCustomWhereClause(whereClause: string): string {
        if (whereClause != null) {
            if (this.$scope.options != null && _.size(this.$scope.options.customWhere)) {

                if (whereClause.length === 0) {
                    return this.$scope.options.customWhere
                }

                return this.createCustomQuery(whereClause, this.$scope.options.customWhere)
            }
            return whereClause
        }
    }

    private createCustomQuery(where: string, customWhere: string): string {
        if (where != null && customWhere != null) {
            if (where.indexOf('and') === -1) {
                where = '( ' + where + ' )'
            }
            if (where[where.length - 1] === ')') {
                return where.slice(0, where.length - 1) + 'and ' + '( ' + customWhere + ' ) )'
            }
        }
    }

    // #endregion

    // #region Export Control

    private startExport(): void {
        this.onExportStarted()

        const exportMethod: ExportFilterMethod = this.$scope.options != null && this.$scope.options.customExportMethod != null
            ? this.$scope.options.customExportMethod
            : this.userSessionService.getDataProvider().System.export

        _.forEach(this.currentParams, (param: OpExportPluginParam) => {
            const paramDataType: OpDataType = _.find(this.paramDataTypes, (dataType: OpDataType) => {
                return dataType.idDataType === param.idDataType
            })

            if (paramDataType != null) {
                param.defaultValue = ConvertHelper.prepareDataValue(param.defaultValue, paramDataType)
            }
        })

        exportMethod(this.referenceType, this.columnInfos, this.whereClause, this.currentPlugin.name, this.currentParams)
            .then((file: OpFile) => {
                this.onExportSucceeded(file)
                this.saveFile(file)
            })
            .fail((error: any) => {
                this.onExportError(error)

                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))

                LogHelper.warn(ExportController.LOG_PREFIX, `Unable to export data. Error: ${errorMsg}.`)
            })
            .always(() => {
                this.onExportFinished()
            })
    }

    private startExportWithParams(): void {
        if (_.size(this.currentPlugin.params) > 0) {
            this.getDataTypeInGroupFromImportParams(this.currentPlugin.params)
                .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                    this.paramDataTypes = _.map(dataTypeInGroup, (dtg: OpDataTypeInGroup) => {
                        return dtg.dataType
                    })

                    if (dataTypeInGroup != null) {
                        const createEditOptions: CreateEditInitParams = this.getCreateEditOptionsFromDataTypeInGroup(dataTypeInGroup)

                        this.openParamsPopup(createEditOptions)
                    }
                    else {
                        LogHelper.warn(ExportController.LOG_PREFIX, "Unable to load dataTypeInGroup from export params.")
                    }
                })
        }
        else {
            this.startExport()
        }
    }

    private saveFile(file: OpFile): void {
        if (file != null && file.fileBytes != null) {
            FileComponent.OpenFileSaver(file)
        }
        else {
            ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))

            LogHelper.warn(ExportController.LOG_PREFIX, "The exported file is null or empty.")
        }
    }

    private resetExport(columnInfos: ColumnInfo[], whereClause: string): void {
        this.columnInfos = columnInfos
        this.whereClause = whereClause

        this.currentParams = null
    }

    // #endregion

    // #region Events

    private onInitialized(): void {
        if (_.isFunction(this.$scope.options.onInitialized)) {
            this.$scope.options.onInitialized(this)
        }
    }

    private onExportStarted(): void {
        if (_.isFunction(this.$scope.options.onExportStarted)) {
            this.$scope.options.onExportStarted()
        }
    }

    private onExportSucceeded(file: OpFile): void {
        if (_.isFunction(this.$scope.options.onExportSucceeded)) {
            this.$scope.options.onExportSucceeded(file)
        }
    }

    private onExportFinished(): void {
        if (_.isFunction(this.$scope.options.onExportFinished)) {
            this.$scope.options.onExportFinished()
        }
    }

    private onExportError(error: any): void {
        if (_.isFunction(this.$scope.options.onExportError)) {
            this.$scope.options.onExportError(error)
        }
    }

    // #endregion

    // #region Configuration Popover

    private onConfigurationDownloadButtonClick(): void {
        this.startExportWithParams()
        this.toggleConfigurationPopover()
    }

    private onConfigurationPopupInitialized(e: any): void {
        this.configurationPopover = e.component
    }

    private onConfigurationPluginSelectBoxChanged(e: any): void {
        this.currentPlugin = e.selectedItem
    }

    private setPluginPopoverOptions(options: ExportOptions, loadedPlugins: OpExportPlugin[], defaultPlugin: OpExportPlugin): void {
        this.$scope.configurationPopoverOptions = {
            width: 400,
            showTitle: true,
            title: options.pluginOptions.popupName,
            target: options.pluginOptions.popoverTarget,

            onInitialized: (e: any) => this.onConfigurationPopupInitialized(e)
        }

        this.$scope.configurationPluginSelectBoxOptions = {
            placeholder: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),
            dataSource: [defaultPlugin, ...loadedPlugins],
            displayExpr: 'toString',
            value: defaultPlugin,

            onSelectionChanged: (e: any) => this.onConfigurationPluginSelectBoxChanged(e)
        }

        this.$scope.configurationDownloadButtonOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Download),

            onClick: () => this.onConfigurationDownloadButtonClick()
        }
    }

    private toggleConfigurationPopover(): void {
        if (this.configurationPopover.option('visible')) {
            this.configurationPopover.hide()
        }
        else {
            this.configurationPopover.show()
        }
    }

    // #endregion

    // #region Params Popup

    private getCreateEditOptionsFromDataTypeInGroup(dataTypeInGroup: OpDataTypeInGroup[]): CreateEditInitParams {
        const section: CreateEditSection = {
            name: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Basics),
            type: CommonCreateEdit.ReferenceObjectBaseParams,
            dataTypesToEdit: dataTypeInGroup,
            dataTypesRequired: dataTypeInGroup
        }

        return {
            moduleName: this.$scope.options.pluginOptions.moduleName,
            idReferenceType: this.currentPlugin.type,
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: null,
            groupReferenceValue: null,
            referenceObjectId: null,
            referenceObject: null,
            sections: [section],
            useBusinessObjects: true
        }
    }

    private getDataTypeInGroupFromImportParams(pluginParams: OpExportPluginParam[]): JQueryPromise<OpDataTypeInGroup[]> {
        const paramIdDataTypes: number[] = pluginParams.map((pluginParam: OpExportPluginParam) => pluginParam.idDataType)

        return $.when(
            this.userSessionService
                .getDataProvider()
                .DataTypeGroup
                .getDataTypeInGroup(Enums.DataTypeGroup.WEBSIMAX_EXPORT_PLUGIN_PARAMS, Enums.ReferenceType.IdDistributor, this.userSessionService.getLoggedOperator().idDistributor),
            this.userSessionService
                .getDataProvider()
                .DataType
                .getDataTypes(paramIdDataTypes)
                .then((paramsDataTypes: OpDataType[]) => {
                    if (paramsDataTypes != null && paramsDataTypes.length > 0) {
                        return paramsDataTypes.map((dataType: OpDataType) => {
                            // Cloning datatypes so as to set different default values.
                            const dataTypeClone = OpDataType.restore(dataType)
                            const pluginParam: OpExportPluginParam = _.find(
                                pluginParams,
                                (pluginParam: OpExportPluginParam) => pluginParam.idDataType === dataTypeClone.idDataType
                            )

                            dataTypeClone.defaultValue = pluginParam.defaultValue

                            return this.dataTypeService.dataTypeToDataTypeInGroup(dataTypeClone)
                        })
                    }

                    return null
                })
        )
            .then((descrDataTypeInGroup: OpDataTypeInGroup[], parametersDataTypeInGroup: OpDataTypeInGroup[]) => {
                if (descrDataTypeInGroup != null && descrDataTypeInGroup.length > 0 && parametersDataTypeInGroup != null && parametersDataTypeInGroup.length > 0) {
                    parametersDataTypeInGroup.forEach((parameter: OpDataTypeInGroup) => {
                        const dtigDescr: OpDataTypeInGroup = _.find(descrDataTypeInGroup, (dtigDescr: OpDataTypeInGroup) => parameter.idDataType == dtigDescr.idDataType && dtigDescr.descr != null)
                        if (dtigDescr != null && parameter.dataType != null) {
                            parameter.dataType.descr = dtigDescr.descr
                        }
                    })
                }

                return parametersDataTypeInGroup
            })
    }

    private getExportPluginParamsFromData(opObject: IOpObject): OpExportPluginParam[] {
        return opObject.dataList
            .toArray()
            .map((data: IOpData) => {
                const newParam: OpExportPluginParam = new OpExportPluginParam(null)
                const currentParam: OpExportPluginParam = _.find(this.currentPlugin.params, (param: OpExportPluginParam) => param.idDataType === data.idDataType)

                newParam.idDataType = currentParam.idDataType
                newParam.defaultValue = currentParam.defaultValue
                newParam.isInputParam = true
                newParam.isVisible = true
                newParam.value = data.value

                return newParam
            })
    }

    private onParamsSave(paramsObject: IOpObject): ng.IPromise<IOpObject> {
        this.$scope.$broadcast(EventsNames.CLOSE_CREATE_EDIT_POPUP, true)
        this.$scope.$root.$broadcast(EventsNames.HIDE_LOADING_INDICATOR)

        this.currentParams = this.getExportPluginParamsFromData(paramsObject)

        this.startExport()

        const deferred = this.$q.defer<IOpObject>()
        deferred.resolve(paramsObject)
        return deferred.promise
    }

    private openParamsPopup(createEditOptions: CreateEditInitParams): void {
        this.$scope.$broadcast(EventsNames.SHOW_CREATE_EDIT_POPUP)
        this.$scope.$broadcast(EventsNames.OPEN_CREATE_EDIT_POPUP, createEditOptions)
    }

    private setParamsPopupOptions(options: ExportOptions): void {
        this.$scope.paramsCreateEditOptions = {
            localStorageVarForSavedEntity: '',
            referenceType: options.referenceType,
            succsessMessage: '',
            title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExportParameters),
            type: '',

            onSave: (objectToSave: IOpObject) => this.onParamsSave(objectToSave),

            onClose: null,
            onBulkSave: null,
            onSubmit: null,
            submitBulkEdit: null,
        }
    }

    // #endregion

    // #region Plugins

    private createDefaultPlugin(): OpExportPlugin {
        const descr: OpDescr = new OpDescr()
        const plugin: OpExportPlugin = new OpExportPlugin()

        descr.description = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CurrentView)

        plugin.name = this.$scope.options.defaultExportDLL != null ? this.$scope.options.defaultExportDLL : ExportController.DEFAULT_EXPORT_DLL
        plugin.description = descr

        return plugin
    }

    private loadPlugins(options: ExportOptions): JQueryPromise<OpExportPlugin[]> {
        return this.userSessionService
            .getDataProvider().Operator
            .getOperatorSettings(this.userSessionService.getIdLoggedOperator(), [Enums.SimaxOperatorSetting.ExportPlugin])
            .then((settings: [OpExportPlugin[]]) => {
                if (_.isArray(settings) && settings[0] != null) {
                    const plugins: OpExportPlugin[] = settings[0]
                    const referencePlugins: OpExportPlugin[] = plugins.filter((plugin: OpExportPlugin) => plugin.type === options.referenceType)

                    if (referencePlugins.length > 0) {
                        return referencePlugins
                    }

                    return null
                }

                return null
            })
    }

    // #endregion
}